import React, { createContext, useCallback, useContext, useState } from "react";
import { isBookingEnabled } from "@/config/booking";
import { trackEvent } from "@/utils/analytics";

interface BookingContextValue {
  isOpen: boolean;
  location: string;
  openBooking: (location?: string) => void;
  closeBooking: () => void;
}

const BookingContext = createContext<BookingContextValue | null>(null);

export const BookingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [location, setLocation] = useState("");

  const openBooking = useCallback((loc = "") => {
    trackEvent("book_call_open", { location: loc });
    if (!isBookingEnabled()) {
      const target = document.getElementById("contact-section");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // contact-section only exists on LandingPage — navigate there from other routes
        window.location.assign("/#contact-section");
      }
      return;
    }
    setLocation(loc);
    setIsOpen(true);
  }, []);

  const closeBooking = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <BookingContext.Provider value={{ isOpen, location, openBooking, closeBooking }}>
      {children}
    </BookingContext.Provider>
  );
};

export function useBooking(): BookingContextValue {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used within BookingProvider");
  return ctx;
}
